import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import api from "services/api";
import { MdArrowBack, MdTv, MdAdd, MdDelete, MdPlayCircle } from "react-icons/md";

const SeriesDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingSeason, setAddingSeason] = useState(false);
  const [addingEpisodeTo, setAddingEpisodeTo] = useState(null);
  const [activeSeasonId, setActiveSeasonId] = useState(null);
  
  // Episode form state
  const [epForm, setEpForm] = useState({ episodeNumber: "", title: "", overview: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [uploadingEpisode, setUploadingEpisode] = useState(false);
  // Percentage of the episode file sent so far — a multi-gigabyte upload needs
  // to show movement, not a spinner that could mean anything.
  const [uploadPercent, setUploadPercent] = useState(0);

  const loadSeries = async () => {
    try {
      setLoading(true);
      const data = await api.getTitle(id);
      setSeries(data);
    } catch (e) {
      console.error("Failed to load series:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadSeries();
  }, [id]);

  useEffect(() => {
    if (series?.seasons?.length > 0) {
      if (!activeSeasonId || !series.seasons.find(s => s.id === activeSeasonId)) {
        setActiveSeasonId(series.seasons[0].id);
      }
    }
  }, [series]);

  const handleAddSeason = async () => {
    if (addingSeason) return;
    const nextSeason = series.seasons.length > 0 
      ? Math.max(...series.seasons.map(s => s.seasonNumber)) + 1 
      : 1;
    
    const seasonNumberStr = window.prompt("Enter Season Number:", nextSeason);
    if (!seasonNumberStr) return;
    const seasonNumber = parseInt(seasonNumberStr, 10);
    if (isNaN(seasonNumber)) return alert("Invalid season number.");

    setAddingSeason(true);
    try {
      await api.createSeason(series.id, seasonNumber);
      await loadSeries();
    } catch (err) {
      alert(err.message || "Failed to create season");
    } finally {
      setAddingSeason(false);
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!window.confirm("Delete this season and ALL its episodes?")) return;
    try {
      await api.deleteSeason(seasonId);
      await loadSeries();
    } catch (err) {
      alert(err.message || "Failed to delete season");
    }
  };

  const handleAddEpisode = async (e, seasonId) => {
    e.preventDefault();
    if (!epForm.episodeNumber || !epForm.title || !videoFile) return alert("Fill required fields and select a video.");
    
    setUploadingEpisode(true);
    try {
      // 1. Upload video file to R2
      setUploadPercent(0);
      const videoKey = await api.uploadFile(videoFile, 'video', (ratio) =>
        setUploadPercent(Math.round(ratio * 100)));

      // 2. Create the episode
      await api.createEpisode(seasonId, { ...epForm, videoKey });
      
      setAddingEpisodeTo(null);
      setEpForm({ episodeNumber: "", title: "", overview: "" });
      setVideoFile(null);
      await loadSeries();
    } catch (err) {
      alert(err.message || "Failed to add episode");
    } finally {
      setUploadingEpisode(false);
    }
  };

  const handleDeleteEpisode = async (episodeId) => {
    if (!window.confirm("Delete this episode?")) return;
    try {
      await api.deleteEpisode(episodeId);
      await loadSeries();
    } catch (err) {
      alert(err.message || "Failed to delete episode");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center pt-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center pt-20">
        <p className="text-xl font-bold text-navy-700 dark:text-white">Series not found.</p>
        <button onClick={() => navigate("/admin/series")} className="mt-4 text-brand-500 hover:underline">
          Return to Series
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Header / Back Button */}
      <div className="mt-3 flex items-center justify-between">
        <button 
          onClick={() => navigate("/admin/series")}
          className="flex items-center gap-2 rounded-xl bg-lightPrimary px-4 py-2 text-sm font-bold text-gray-600 transition hover:bg-gray-200 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
        >
          <MdArrowBack className="h-5 w-5" /> Back to Series
        </button>
      </div>

      <Card extra="w-full overflow-hidden p-6 relative">
        <div className="flex flex-col gap-8 md:flex-row">
          {/* Poster Section */}
          <div className="w-full shrink-0 md:w-1/3 lg:w-1/4">
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-2xl shadow-xl">
              {series.posterUrl ? (
                <img src={series.posterUrl} alt={series.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-100 dark:bg-navy-700">
                  <MdTv className="h-20 w-20 text-gray-300" />
                </div>
              )}
            </div>
          </div>

          {/* Details Section */}
          <div className="flex w-full flex-col justify-center">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-brand-500/10 px-3 py-1 text-sm font-bold text-brand-500">
                {series.year}
              </span>
              {(series.genres || []).map((genre) => (
                <span key={genre} className="rounded-full border border-gray-200 bg-lightPrimary px-3 py-1 text-sm font-medium text-navy-700 dark:border-white/10 dark:bg-navy-800 dark:text-white">
                  {genre}
                </span>
              ))}
            </div>

            <h1 className="mb-2 text-4xl font-bold text-navy-700 dark:text-white">
              {series.title}
            </h1>
            
            <div className="mb-6 text-base font-medium text-gray-600 dark:text-gray-400">
              VJ: <span className="text-brand-500">{series.vj || "None"}</span>
            </div>

            <div className="mb-8 max-w-3xl">
              <h3 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">Overview</h3>
              <p className="text-base leading-relaxed text-gray-600 dark:text-gray-400">
                {series.overview || "No overview available."}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Seasons Section */}
      <div className="mt-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Seasons & Episodes</h2>
        <button 
          onClick={handleAddSeason}
          disabled={addingSeason}
          className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
        >
          <MdAdd className="h-5 w-5" /> Add Season
        </button>
      </div>

      {series.seasons?.length === 0 ? (
        <Card extra="flex flex-col items-center justify-center p-12 w-full border border-dashed border-gray-300 bg-transparent shadow-none dark:border-white/20">
          <MdTv className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">No Seasons Yet</h4>
          <p className="mt-2 text-sm text-gray-500">Click the button above to add a new season.</p>
        </Card>
      ) : (
        <div className="flex flex-col w-full pb-10">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-white/10 mb-6 overflow-x-auto scrollbar-hide">
            {series.seasons.map((season) => (
              <button
                key={season.id}
                onClick={() => setActiveSeasonId(season.id)}
                className={`whitespace-nowrap px-6 py-3 font-bold transition-colors ${
                  activeSeasonId === season.id
                    ? "border-b-2 border-brand-500 text-brand-500"
                    : "text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                Season {season.seasonNumber}
              </button>
            ))}
          </div>

          {/* Active Season Content */}
          {series.seasons.map((season) => (
            season.id === activeSeasonId && (
              <Card key={season.id} extra="w-full p-6">
                <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4 dark:border-white/10">
                  <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                    Season {season.seasonNumber}
                    <span className="ml-3 text-sm font-medium text-gray-500">({season.episodes.length} episodes)</span>
                  </h3>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setAddingEpisodeTo(addingEpisodeTo === season.id ? null : season.id)}
                      className="flex items-center gap-1.5 rounded-lg bg-lightPrimary px-3 py-1.5 text-sm font-bold text-brand-500 hover:bg-brand-50 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                      <MdAdd className="h-4 w-4" /> {addingEpisodeTo === season.id ? "Cancel" : "Add Episode"}
                    </button>
                    <button 
                      onClick={() => handleDeleteSeason(season.id)}
                      className="flex items-center justify-center rounded-lg p-2 text-red-500 transition hover:bg-red-50 dark:hover:bg-red-500/10"
                      title="Delete Season"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Add Episode Form */}
                {addingEpisodeTo === season.id && (
                  <div className="mb-6 rounded-xl border border-brand-500/20 bg-brand-500/5 p-4 dark:bg-brand-400/10">
                    <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">Add New Episode</h4>
                    <form onSubmit={(e) => handleAddEpisode(e, season.id)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold text-navy-700 dark:text-white">Episode Number *</label>
                        <input 
                          required 
                          type="number"
                          value={epForm.episodeNumber} 
                          onChange={(e) => setEpForm({ ...epForm, episodeNumber: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" 
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-navy-700 dark:text-white">Episode Title *</label>
                        <input 
                          required 
                          value={epForm.title} 
                          onChange={(e) => setEpForm({ ...epForm, title: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" 
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-bold text-navy-700 dark:text-white">Upload Video *</label>
                        <input 
                          required 
                          type="file"
                          accept="video/mp4,video/webm"
                          onChange={(e) => setVideoFile(e.target.files[0])}
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" 
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-bold text-navy-700 dark:text-white">Overview</label>
                        <textarea 
                          rows={2}
                          value={epForm.overview} 
                          onChange={(e) => setEpForm({ ...epForm, overview: e.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white" 
                        />
                      </div>
                      <div className="md:col-span-2 flex justify-end">
                        <button 
                          type="submit" 
                          disabled={uploadingEpisode}
                          className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {uploadingEpisode && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                          {uploadingEpisode
                            ? `Uploading… ${uploadPercent}%`
                            : "Save Episode"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Episodes List */}
                {season.episodes.length === 0 ? (
                  <p className="text-sm font-medium text-gray-500 italic">No episodes added yet.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {season.episodes.map((ep) => (
                      <div key={ep.id} className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700">
                        <div className="flex items-start gap-4">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white font-bold text-brand-500 shadow-sm dark:bg-navy-900">
                            {ep.episodeNumber}
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-navy-700 dark:text-white">{ep.title}</h4>
                            {ep.overview && <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">{ep.overview}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 pl-4">
                          {ep.videoUrl && (
                            <a href={ep.videoUrl} target="_blank" rel="noreferrer" className="text-brand-500 hover:text-brand-600">
                              <MdPlayCircle className="h-7 w-7" />
                            </a>
                          )}
                          <button onClick={() => handleDeleteEpisode(ep.id)} className="text-red-500 hover:bg-red-50 rounded-full p-2 dark:hover:bg-red-500/10">
                            <MdDelete className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default SeriesDetails;
